import { formatCredits, monthlyCreditsEnabled } from "./monthly-policy";

/**
 * Numeric prices and balances use legacy accounting units (50 displayed credits each).
 * AI credit pricing — author-side credits for billable AI actions (cover
 * image renders and manual cover-prompt redrafts).
 *
 * Custom-quantity ONE-TIME top-ups (monthly grants are in monthly-policy): the author
 * picks how many credits to buy and pays credits × AI_CREDIT_PRICE_CENTS in
 * a single Stripe Checkout payment. The per-credit rate comes from the
 * AI_CREDIT_PRICE_CENTS env var (integer cents per credit).
 *
 * Credits are WEIGHTED per action (industry norm — cf. Playground/Leonardo/
 * Ideogram): a cover image render costs CREDITS_PER_COVER_IMAGE credits, a
 * manual prompt redraft costs the cheaper CREDITS_PER_REDRAFT. The automatic
 * first draft on page load is always free; each story also gets independent
 * free cover and Wiki generation allowances before paid credits are consumed.
 */

// Per-action credit weights. Credits are a fine unit (1 credit = 0.5¢) so the
// same currency can later meter the AI assistant, whose cost varies per
// message. An image render is the expensive action; a redraft is the cheap
// text call, so it costs a fraction of a render.
export const CREDITS_PER_COVER_IMAGE = 30; // 30 × 0.5¢ = $0.15 retail
export const COVER_IMAGE_CANDIDATE_COUNT = 3;
export const CREDITS_PER_WIKI_IMAGE = 30;
export const WIKI_IMAGE_CANDIDATE_COUNT = 2;
export const CREDITS_PER_REDRAFT = 10; //     10 × 0.5¢ = $0.05 retail

// Each story gets six complete generation batches on each image surface.
// Once a surface's allowance is exhausted, every action draws from the one
// shared paid wallet. Manual prompt redrafts always use that paid wallet.
export const FREE_GENERATIONS_PER_SURFACE = 6;
export const FREE_COVER_CREDITS_PER_STORY =
  FREE_GENERATIONS_PER_SURFACE *
  COVER_IMAGE_CANDIDATE_COUNT *
  CREDITS_PER_COVER_IMAGE;
export const FREE_WIKI_CREDITS_PER_STORY =
  FREE_GENERATIONS_PER_SURFACE *
  WIKI_IMAGE_CANDIDATE_COUNT *
  CREDITS_PER_WIKI_IMAGE;

// Purchase bounds: the minimum clears Stripe's fixed per-charge fee at the
// per-credit rate ($10); the maximum blocks typos and card-testing abuse.
export const MIN_CREDIT_PURCHASE = 2000; //  $10 at 0.5¢
export const MAX_CREDIT_PURCHASE = 100000; // $500

// Quick-pick chips shown on the buy-credits surface ($10 / $15 / $25 at 0.5¢).
export const SUGGESTED_CREDIT_AMOUNTS = [2000, 3000, 5000] as const;

// Cents per credit — fractional (0.5¢). Purchase totals are rounded to whole
// cents for Stripe; per-action costs are whole-credit so they never fract.
const DEFAULT_PRICE_CENTS_PER_CREDIT = 0.5;

/** Cents charged per credit (env-driven; may be fractional, default 0.5). */
export function getAiCreditPriceCents(): number {
  const raw = process.env.AI_CREDIT_PRICE_CENTS;
  if (!raw) return DEFAULT_PRICE_CENTS_PER_CREDIT;
  const cents = Number(raw);
  if (!Number.isFinite(cents) || cents <= 0) {
    throw new Error(
      `Invalid AI_CREDIT_PRICE_CENTS: expected a positive number (cents per credit), got "${raw}"`,
    );
  }
  return cents;
}

export type CreditPriceResult =
  | { ok: true; credits: number; amountInCents: number; priceStr: string }
  | { ok: false; error: string };

/**
 * Pure quantity → price computation. Rejects non-integer or out-of-bounds
 * quantities; the checkout action and the client both validate through this
 * (server-side result is authoritative).
 */
export function computeCreditPriceCents(
  credits: number,
  centsPerCredit: number = getAiCreditPriceCents(),
): CreditPriceResult {
  if (!Number.isInteger(credits)) {
    return { ok: false, error: "Credit amount must be a whole number." };
  }
  if (credits < MIN_CREDIT_PURCHASE) {
    return {
      ok: false,
      error: `Minimum purchase is ${formatCredits(MIN_CREDIT_PURCHASE)} credits.`,
    };
  }
  if (credits > MAX_CREDIT_PURCHASE) {
    return {
      ok: false,
      error: `Maximum purchase is ${formatCredits(MAX_CREDIT_PURCHASE)} credits.`,
    };
  }
  // Round the total to whole cents for Stripe (the per-credit rate may be
  // fractional, e.g. 0.5¢). Quick-pick amounts are even, so this is exact;
  // an odd custom quantity rounds by at most half a cent.
  const amountInCents = Math.round(credits * centsPerCredit);
  return {
    ok: true,
    credits,
    amountInCents,
    priceStr: `$${(amountInCents / 100).toFixed(2)}`,
  };
}

/** Serializable pricing config for client components (env stays server-side). */
export interface AiCreditPricingConfig {
  monthlyPlansEnabled?: boolean;
  centsPerCredit: number;
  min: number;
  max: number;
  suggested: number[];
  creditsPerImage: number;
  creditsPerRedraft: number;
  freeCoverCreditsPerStory: number;
  freeWikiCreditsPerStory: number;
}

export function getAiCreditPricingConfig(): AiCreditPricingConfig {
  return {
    monthlyPlansEnabled: monthlyCreditsEnabled(),
    centsPerCredit: getAiCreditPriceCents(),
    min: MIN_CREDIT_PURCHASE,
    max: MAX_CREDIT_PURCHASE,
    suggested: [...SUGGESTED_CREDIT_AMOUNTS],
    creditsPerImage: CREDITS_PER_COVER_IMAGE,
    creditsPerRedraft: CREDITS_PER_REDRAFT,
    freeCoverCreditsPerStory: FREE_COVER_CREDITS_PER_STORY,
    freeWikiCreditsPerStory: FREE_WIKI_CREDITS_PER_STORY,
  };
}
